import React from "react";

import cx from "classnames";

import { coalesceFirstTruthy } from "@kazoohr/helpers";

import { Checkbox } from "../Checkbox";

import styles from "./DataTable.scss";
import { SortableHeader } from "./SortableHeader";
import type { Alignment, TableColumn } from "./types";

interface TableHeaderProps {
  columns: TableColumn[];
  defaultAlignment: Alignment;
  isSelectAllChecked?: boolean;
  onToggleSelectAll?: () => void;
  selectAllStatus?: "checked" | "unchecked" | "half-checked";
  /**
   * Function that is called when a heading column is clicked -- for sorting purposes
   */
  onClick?: (clickedColumnHeaderName: string) => void;
  showCheckboxes?: boolean;
  enableNestedRows?: boolean;
}

export function TableHeader({
  columns,
  isSelectAllChecked,
  onClick,
  onToggleSelectAll,
  defaultAlignment,
  enableNestedRows,
  showCheckboxes,
  selectAllStatus,
}: TableHeaderProps) {
  const sortedColumns = columns.find((column) => column.sort !== undefined);
  const cellStyle = sortedColumns ? "header_cell" : "header_cell_padding";

  function renderCell(column: TableColumn, index: number) {
    if (!enableNestedRows) {
      if (column.isCheckBox) {
        return (
          <Checkbox
            id="select-all"
            data-testid="table-select-all-checkbox"
            checked={isSelectAllChecked}
            onChange={onToggleSelectAll}
          />
        );
      } else {
        return (
          <SortableHeader
            column={column}
            onClick={onClick}
            sort={column.sort}
          />
        );
      }
    } else {
      if (index !== 0) {
        return (
          <SortableHeader
            column={column}
            onClick={onClick}
            sort={column.sort}
          />
        );
      } else {
        const checked = coalesceFirstTruthy(
          selectAllStatus === "checked",
          selectAllStatus === "half-checked",
          isSelectAllChecked
        );
        return (
          <div className={styles["nested-checkbox-header"]}>
            {showCheckboxes && (
              <Checkbox
                id="select-all"
                data-testid="table-select-all-checkbox"
                checked={checked}
                indeterminate={selectAllStatus === "half-checked"}
                onChange={onToggleSelectAll}
              />
            )}
            <SortableHeader
              column={column}
              onClick={onClick}
              sort={column.sort}
            />
          </div>
        );
      }
    }
  }

  return (
    <tr className={styles.header_row}>
      {columns.map((column, index) => {
        const headerCellClassNames = cx(
          styles[cellStyle],
          styles[`align_${column.alignment || defaultAlignment}`]
        );
        const key = `${column.header}-header-cell-${index}`;
        return (
          <td key={key} className={headerCellClassNames}>
            {renderCell(column, index)}
          </td>
        );
      })}
    </tr>
  );
}
